/**
 * Works out the status other people see for one person, from facts the server
 * has already loaded. Pure so the precedence, the working-hours boundaries and
 * the time-zone handling can be tested without a database or a clock.
 *
 * Precedence: a status the person picked > in a meeting > on site > outside
 * working hours > the default. Each automatic step only runs when the person
 * has switched it on in Preferences > Availability.
 */
import type { ResolvedPresenceStatus } from '../types/presence.ts';
import { PRESENCE_STATE_LABELS, normaliseStoredPresenceStatus } from './presenceVocabulary.ts';

export interface AutoPresenceSettings {
  autoMeeting: boolean;
  autoOnSite: boolean;
  autoAfterHours: boolean;
}

/** Mirrors DEFAULT_PREFERENCES.availability, for people with no preferences row. */
export const DEFAULT_AUTO_PRESENCE_SETTINGS: AutoPresenceSettings = {
  autoMeeting: true,
  autoOnSite: false,
  autoAfterHours: true,
};

type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export interface PresenceScheduleDay {
  enabled?: boolean;
  start?: string;
  end?: string;
}

export interface PresenceSchedule {
  timezone?: string;
  days?: Partial<Record<DayKey, PresenceScheduleDay>>;
}

/** A span of time the person is taken up: a Quantis calendar event or a Google busy slot. */
export interface BusyBlock {
  start: string;
  end: string;
}

export interface AttendanceEvent {
  eventType: string;
  recordedAt: string;
}

export interface PresenceFacts {
  now: Date;
  manual: { status: unknown; label?: string | null; until?: string | null } | null;
  settings: AutoPresenceSettings;
  busy: BusyBlock[];
  outOfOffice: BusyBlock | null;
  attendance: AttendanceEvent[];
  schedule: PresenceSchedule | null;
  timeZone: string;
  /**
   * Whether anything vouches for the person being around by default. Without
   * it (no connected calendar) the default is no dot at all, so people who
   * have never signed in are not shown as available.
   */
  hasLiveSignal: boolean;
}

export type PresenceSource = 'manual' | 'out_of_office' | 'meeting' | 'on_site' | 'after_hours' | 'default';

export interface DerivedPresence {
  state: ResolvedPresenceStatus | null;
  label: string;
  until?: string;
  source: PresenceSource;
}

/** A clock-in older than this with no clock-out is a forgotten clock-out, not someone still on site. */
export const MAX_OPEN_CLOCK_IN_MS = 16 * 60 * 60 * 1000;

const DEFAULT_DAYS: Record<DayKey, Required<PresenceScheduleDay>> = {
  monday:    { enabled: true,  start: '08:00', end: '18:00' },
  tuesday:   { enabled: true,  start: '08:00', end: '18:00' },
  wednesday: { enabled: true,  start: '08:00', end: '18:00' },
  thursday:  { enabled: true,  start: '08:00', end: '18:00' },
  friday:    { enabled: true,  start: '08:00', end: '18:00' },
  saturday:  { enabled: false, start: '09:00', end: '17:00' },
  sunday:    { enabled: false, start: '09:00', end: '17:00' },
};

const WEEKDAY_KEYS: Record<string, DayKey> = {
  Mon: 'monday', Tue: 'tuesday', Wed: 'wednesday', Thu: 'thursday',
  Fri: 'friday', Sat: 'saturday', Sun: 'sunday',
};

export function isValidTimeZone(value: unknown): value is string {
  if (typeof value !== 'string' || !value || value === 'auto') return false;
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/** Preferences time zone, unless it is 'auto'; then the profile's, then the schedule's, then London. */
export function resolvePresenceTimeZone(...candidates: unknown[]): string {
  return candidates.find(isValidTimeZone) ?? 'Europe/London';
}

function toMinutes(value: string): number {
  const [hour = '0', minute = '0'] = value.split(':');
  return Number(hour) * 60 + Number(minute);
}

/** Whether `date` falls inside the schedule, read in `timeZone`. A missing schedule or day takes the default Monday to Friday, 08:00 to 18:00. */
export function isWithinWorkingSchedule(date: Date, schedule: PresenceSchedule | null, timeZone: string): boolean {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  const dayKey = WEEKDAY_KEYS[get('weekday')] ?? 'monday';
  const stored = schedule?.days?.[dayKey];
  const fallback = DEFAULT_DAYS[dayKey];
  const enabled = typeof stored?.enabled === 'boolean' ? stored.enabled : fallback.enabled;
  if (!enabled) return false;

  const current = toMinutes(`${get('hour')}:${get('minute')}`);
  const start = toMinutes(stored?.start || fallback.start);
  const end = toMinutes(stored?.end || fallback.end);
  if (start === end) return false;
  // An end before the start is an overnight shift that runs past midnight.
  return start < end
    ? current >= start && current < end
    : current >= start || current < end;
}

function inProgress(block: BusyBlock, now: number): boolean {
  const start = Date.parse(block.start);
  const end = Date.parse(block.end);
  return Number.isFinite(start) && Number.isFinite(end) && start <= now && end > now;
}

/** The end of the busy spell under way now, or null when nothing is on. Back-to-back blocks are not chained. */
export function currentBusyUntil(blocks: BusyBlock[], now: Date): string | null {
  const at = now.getTime();
  let until: string | null = null;
  for (const block of blocks) {
    if (inProgress(block, at) && (until === null || Date.parse(block.end) > Date.parse(until))) {
      until = block.end;
    }
  }
  return until;
}

/** Whether the latest clock-in or clock-out is a recent clock-in. Geofence events are ignored: the setting is about clocking in. */
export function isClockedIn(events: AttendanceEvent[], now: Date): boolean {
  const at = now.getTime();
  let latest: AttendanceEvent | null = null;
  for (const event of events) {
    if (event.eventType !== 'clock_in' && event.eventType !== 'clock_out') continue;
    const recorded = Date.parse(event.recordedAt);
    if (!Number.isFinite(recorded) || recorded > at) continue;
    if (!latest || recorded > Date.parse(latest.recordedAt)) latest = event;
  }
  if (latest?.eventType !== 'clock_in') return false;
  return at - Date.parse(latest.recordedAt) < MAX_OPEN_CLOCK_IN_MS;
}

export function derivePresence(facts: PresenceFacts): DerivedPresence {
  const { now, settings } = facts;

  const manualState = normaliseStoredPresenceStatus(facts.manual?.status);
  const manualUntil = facts.manual?.until ?? null;
  const manualExpired = manualUntil ? Date.parse(manualUntil) <= now.getTime() : false;
  if (manualState && !manualExpired) {
    return {
      state: manualState,
      label: facts.manual?.label || PRESENCE_STATE_LABELS[manualState],
      ...(manualUntil ? { until: manualUntil } : {}),
      source: 'manual',
    };
  }

  if (settings.autoMeeting) {
    if (facts.outOfOffice && inProgress(facts.outOfOffice, now.getTime())) {
      return { state: 'away', label: 'Out of office', until: facts.outOfOffice.end, source: 'out_of_office' };
    }
    const busyUntil = currentBusyUntil(facts.busy, now);
    if (busyUntil) {
      return { state: 'in_meeting', label: PRESENCE_STATE_LABELS.in_meeting, until: busyUntil, source: 'meeting' };
    }
  }

  if (settings.autoOnSite && isClockedIn(facts.attendance, now)) {
    return { state: 'on_site', label: PRESENCE_STATE_LABELS.on_site, source: 'on_site' };
  }

  if (settings.autoAfterHours && !isWithinWorkingSchedule(now, facts.schedule, facts.timeZone)) {
    return { state: 'away', label: 'Outside working hours', source: 'after_hours' };
  }

  return facts.hasLiveSignal
    ? { state: 'available', label: PRESENCE_STATE_LABELS.available, source: 'default' }
    : { state: null, label: '', source: 'default' };
}
